<template>
  <div
    :id="getOverlayContainerId(overlay)"
    :class="[
      'overlay',
      overlay.signalName,
      { visible: isVisible, hidden: isHidden },
    ]"
    :style="getStyle(overlay.instance.boundingBox)"
    @mouseover="setOverlayVisible(true)"
    @mouseleave="setOverlayVisible(false)"
  >
    <component
      :is="relevantComponent"
      :bounding-box="getTransformWithoutRotation(overlay.instance.boundingBox)"
      :bounding-boxes="
        overlay.instance.bounding_boxes.map(getTransformWithoutRotation)
      "
      :style="getCSSRotation()"
      :color="overlay.color"
      :masked-text="overlay.instance.hidden_text"
      :zoom-factor="zoomFactor"
      :container-id="getOverlayContainerId(overlay)"
      :tooltip-text="getTooltipText()"
    >
    </component>
    <div
      :class="{ visible: isVisible, 'keep-visible': keepVisible }"
      class="overlay-content-wrapper"
      data-testid="overlay-content"
      @mouseover="setOverlayVisible(true)"
      @mouseleave="setOverlayVisible(false)"
    >
      <slot name="overlay-content"></slot>
    </div>
  </div>
</template>

<script>
import * as fileTypes from '../../../constants/fileTypes';
import TextIssuesOverlay from './TextIssuesOverlay';
import BasicOverlay from './BasicOverlay';
import MaskingOverlay from './MaskingOverlay';
import FlatOverlay from './FlatOverlay';
import EditedTextOverlay from './EditedTextOverlay';
import PdfAlignmentOverlay from './PdfAlignmentOverlay';
import * as signalNames from '../../../constants/signalNames';
import signalConfig from '../../../constants/signalConfig';
import { closeAllTooltips } from '@inscribe/signal';

export default {
  props: {
    fileType: {
      type: String,
      required: true,
    },
    documentPage: {
      type: Node,
      default: () => null,
    },
    imagePositionMultiplier: {
      type: Object,
      required: true,
    },
    rotation: {
      type: Number,
      required: true,
    },
    overlay: {
      type: Object,
      required: true,
    },
    zoomFactor: {
      type: Number,
      required: true,
    },
    keepVisible: {
      type: Boolean,
      required: false,
    },
    showTooltip: {
      type: Boolean,
      required: false,
      default: true,
    },
    isHidden: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      textMounted: false,
      isVisible: false,
      visibilityTimeout: null,
    };
  },
  computed: {
    relevantComponent() {
      if (this.overlay.signalName) {
        switch (this.overlay.signalName) {
          case signalNames.FONTS:
            return TextIssuesOverlay;
          case signalNames.EDITED_TEXT:
            return EditedTextOverlay;
          case signalNames.MASKING:
            return MaskingOverlay;
          case signalNames.MASKING_LIMITED:
            return MaskingOverlay;
          case signalNames.FINGERPRINT:
            return FlatOverlay;
          case signalNames.ANNOTATIONS:
            return TextIssuesOverlay;
          case signalNames.PDF_ALIGNMENT:
            return PdfAlignmentOverlay;
          case signalNames.FONT_ANOMALY:
            return TextIssuesOverlay;
          case signalNames.NAME_FORMAT_ANOMALY:
            return TextIssuesOverlay;
          case signalNames.ADDRESS_FORMAT_ANOMALY:
            return TextIssuesOverlay;
          case signalNames.DATE_FORMAT_ANOMALY:
            return TextIssuesOverlay;
          case signalNames.TEXT_ALIGNMENT:
            return TextIssuesOverlay;
          case signalNames.SUMMARY_NOT_BALANCED:
            return TextIssuesOverlay;
          case signalNames.GENERIC_DATA:
            return TextIssuesOverlay;
          case signalNames.PAYSLIP_MISCALCULATION:
            return TextIssuesOverlay;
          default:
            return BasicOverlay;
        }
      } else {
        return FlatOverlay;
      }
    },
  },
  watch: {
    zoomFactor() {
      this.textMounted = false;
      this.checkTextMounted();
    },
  },
  mounted() {
    this.checkTextMounted();
  },
  methods: {
    getOverlayContainerId(overlay) {
      return `box_${overlay.instance.boundingBox.top}_${overlay.color}`
        .replace(/#/g, '')
        .replace(/\./g, '')
        .replace(/ /g, '');
    },
    setOverlayVisible(value) {
      if (this.keepVisible) {
        return;
      }
      this.visibilityTimeout && clearTimeout(this.visibilityTimeout);
      if (value) {
        closeAllTooltips();
        this.isVisible = true;
      } else {
        this.visibilityTimeout = setTimeout(() => {
          this.isVisible = false;
        }, 500);
      }
    },
    getTooltipText() {
      if (!this.overlay.signalName && this.showTooltip) {
        return this.overlay.label;
      }
    },
    checkTextMounted() {
      if (document.querySelector('.textLayer span')) {
        this.textMounted = true;
      } else {
        setTimeout(() => {
          this.checkTextMounted();
        }, 350);
      }
    },
    getStyle(boundingBox, color) {
      const { height, ...restBox } = this.getTransformWithRotation(
        boundingBox,
        color,
      );
      return {
        ...restBox,
      };
    },
    getCSSRotation() {
      return {
        transformOrigin: `left top`,
        transform: `rotate(${this.rotation}deg)`,
      };
    },
    getScaleXY() {
      const pdfScale = this.zoomFactor * 1.333333;
      return this.fileType === fileTypes.IMAGE
        ? {
            x: this.imagePositionMultiplier.x * pdfScale,
            y: this.imagePositionMultiplier.y * pdfScale,
          }
        : { x: pdfScale, y: pdfScale };
    },
    getScale(boundingBox) {
      const { top, bottom, x0, x1 } = boundingBox;
      const { x, y } = this.getScaleXY();

      return {
        top: top * y,
        height: bottom * y - top * y,
        left: x0 * x,
        width: x1 * x - x0 * x,
      };
    },
    useSpecifiedColor(signalName) {
      return [
        signalNames.FONT_ANOMALY,
        signalNames.NAME_FORMAT_ANOMALY,
        signalNames.ADDRESS_FORMAT_ANOMALY,
        signalNames.DATE_FORMAT_ANOMALY,
        signalNames.TEXT_ALIGNMENT,
        signalNames.GENERIC_DATA,
        signalNames.SUMMARY_NOT_BALANCED,
        signalNames.PAYSLIP_MISCALCULATION,
      ].includes(signalName);
    },
    getTransformWithRotation(boundingBox, color) {
      return this.rotateBoundingBox(this.getScale(boundingBox));
    },
    getTransformWithoutRotation(boundingBox) {
      const transformedBoundingBox = this.getScale(boundingBox);
      if (this.useSpecifiedColor(this.overlay.signalName)) {
        transformedBoundingBox.color =
          signalConfig[this.overlay.signalName].color;
      } else {
        transformedBoundingBox.color = boundingBox.color;
      }
      return transformedBoundingBox;
    },
    rotateBoundingBox(box, pixelate = true) {
      const { top, left, width, height } = box;
      if (!this.documentPage) {
        return this.pixelizeCoordinates(box, pixelate);
      }

      const { clientHeight, clientWidth } = this.documentPage;
      const isImage = this.fileType === fileTypes.IMAGE;
      const isNormallyOrientated = this.rotation % 180 === 0;
      const naturalPageHeight =
        isImage || isNormallyOrientated ? clientHeight : clientWidth;
      const naturalPageWidth =
        isImage || isNormallyOrientated ? clientWidth : clientHeight;

      switch (this.rotation) {
        case 90:
          return this.pixelizeCoordinates(
            {
              top: left,
              left: naturalPageHeight - top,
              height: width,
              width: height,
            },
            pixelate,
          );
        case 180:
          return this.pixelizeCoordinates(
            {
              top: naturalPageHeight - top,
              left: naturalPageWidth - left,
              height,
              width,
            },
            pixelate,
          );
        case 270:
          return this.pixelizeCoordinates(
            {
              top: naturalPageWidth - left,
              left: top,
              height: width,
              width: height,
            },
            pixelate,
          );
        case 0:
        default:
          return this.pixelizeCoordinates(box, pixelate);
      }
    },
    pixelizeCoordinates(box, pixelize = true) {
      if (!pixelize) {
        return box;
      }
      const { top, left, width, height } = box;
      return {
        top: `${top}px`,
        height: `${height}px`,
        left: `${left}px`,
        width: `${width}px`,
      };
    },
    boxKey(box) {
      return [box.x0, box.x1, box.top, box.bottom].join('-');
    },
  },
};
</script>

<style lang="scss">
.overlay {
  position: absolute;
  border-radius: $border-radius-default;
  z-index: 50;

  &:not(.masking) {
    mix-blend-mode: multiply;
  }

  &.visible {
    z-index: 100;
  }

  &.hidden {
    display: none;
  }

  .overlay-child {
    border-radius: $border-radius-default;
  }

  .overlay-child {
    position: relative;
    height: 100%;
    width: 100%;
  }

  &.fonts .overlay-child {
    border-radius: 0;
    border: 0 !important;
    padding: 0;
  }

  &.fingerprint,
  &.fonts {
    border: 0 !important;
  }

  .overlay-content-wrapper {
    $arrow-size: 16px;
    display: none;
    position: absolute;
    bottom: calc(100% - 6px);
    left: calc(50% - 125px);
    padding-bottom: 16px;
    width: 250px;
    z-index: 60;

    &.visible,
    &.keep-visible {
      display: block;
      z-index: 100;
      --first-line-height: 12px;
    }

    .overlay-content {
      background: $color-navy-blue-80;
      overflow: visible;
      width: 100%;

      &::before {
        $arrow-size: 16px;

        background: $color-navy-blue-80;
        bottom: calc($arrow-size/2);
        content: '';
        height: $arrow-size;
        left: calc(50% - ($arrow-size / 2));
        position: absolute;
        transform: rotate(45deg);
        width: $arrow-size;
      }

      .requirement-summary,
      .signal-summary {
        .signal-icon-wrapper {
          background: $color-white;

          .signal-icon {
            color: $color-navy-blue-80;
          }
        }

        .signal-name {
          color: $color-white;
        }

        .signal-description {
          color: $color-raven-10;
        }
      }

      .signal-help-text {
        color: $color-navy-blue-20;
      }

      .signal-overlay-actions {
        padding-left: $padding-card;

        .signal-visibility-toggle {
          color: $color-navy-blue-20;
        }

        .report-problem .btn-link,
        .report-problem span {
          color: $color-navy-blue-20;
          font-size: $font-size-small !important;
          line-height: $font-size-secondary;
        }

        &.top {
          position: absolute;
          right: $padding-input-x;
          top: 4px;
        }
      }
    }
  }

  .hidden-text {
    display: none;
  }
}
</style>
